import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Checkbox,
  Chip,
  FilterOptionsState,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { useMemo, useState } from 'react';

const SELECT_ALL = '__select_all__';

interface Props {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
}

const filter = createFilterOptions<string>();

/**
 * Multi-select for document type names with type-to-filter and a Select-all /
 * Clear-all row. The "Select all" toggle respects the active search filter: it
 * selects/clears only the currently-visible options, preserving selections that
 * are hidden by the filter.
 */
export const DocumentTypeMultiSelect: React.FC<Props> = ({
  options,
  value,
  onChange,
  label = 'Current document types',
}) => {
  const [inputValue, setInputValue] = useState('');

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.localeCompare(b)),
    [options],
  );

  // Options currently visible given the search text (excludes the Select-all row).
  const visibleOptions = useMemo(
    () => filter(sortedOptions, { inputValue, getOptionLabel: (o) => o }),
    [sortedOptions, inputValue],
  );

  const allVisibleSelected =
    visibleOptions.length > 0 && visibleOptions.every((opt) => value.includes(opt));
  const someVisibleSelected = visibleOptions.some((opt) => value.includes(opt));

  // Keep the Select-all row pinned at the top of the filtered list.
  const filterOptions = (opts: string[], state: FilterOptionsState<string>) => {
    const filtered = filter(
      opts.filter((opt) => opt !== SELECT_ALL),
      state,
    );
    return [SELECT_ALL, ...filtered];
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      onChange(value.filter((v) => !visibleOptions.includes(v)));
    } else {
      onChange(Array.from(new Set([...value, ...visibleOptions])));
    }
  };

  return (
    <Autocomplete
      multiple
      size='small'
      fullWidth
      disableCloseOnSelect
      options={[SELECT_ALL, ...sortedOptions]}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        // Don't wipe the search box when an option is selected (keeps filter active).
        if (reason !== 'reset') {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, val) => option === val}
      onChange={(_, newValue) => {
        const next = newValue as string[];
        if (next.includes(SELECT_ALL)) {
          toggleSelectAll();
          return;
        }
        onChange(next);
      }}
      getOptionLabel={(option) => (option === SELECT_ALL ? 'Select all' : option)}
      renderOption={(props, option, { selected }) => {
        if (option === SELECT_ALL) {
          return (
            <li {...props} key={SELECT_ALL}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                checkedIcon={<CheckBoxIcon fontSize='small' />}
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                style={{ marginRight: 8 }}
              />
              {allVisibleSelected ? 'Clear all' : 'Select all'}
            </li>
          );
        }
        return (
          <li {...props} key={option}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
              checkedIcon={<CheckBoxIcon fontSize='small' />}
              checked={selected}
              style={{ marginRight: 8 }}
            />
            {option}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        const shown = tagValue.slice(0, 3);
        return (
          <>
            {shown.map((option, index) => (
              <Chip size='small' label={option} {...getTagProps({ index })} key={option} />
            ))}
            {tagValue.length > shown.length && (
              <Chip size='small' label={`+${tagValue.length - shown.length}`} />
            )}
          </>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} size='small' />}
    />
  );
};
